/**
 * PreferencesTab.js
 * =================
 *
 * Account-level preferences inside the Settings shell:
 *
 *  * Appearance — light/dark theme toggle (same ThemeContext switch as
 *    the header user menu; persisted by the theme provider).
 *  * Timezone — stored on the user doc via ``POST /v1/user/update-profile``
 *    and honored by every date rendered in the app (see
 *    utils/datetime.js: a global toLocale* shim + tz-aware date-fns
 *    ``format``, both reading this preference).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Box, MenuItem, Stack, Switch, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import PublicIcon from '@mui/icons-material/Public';
import CircularProgress from '@mui/material/CircularProgress';

import { Surface } from '../dashboard/components/DashboardPrimitives';
import { CMButton } from '../../containers/styledComponents';
import { useTheme as useAppTheme } from '../../theme/ThemeContext';
import { fetchRequest as updateProfile } from '../../redux/actions/auth/updateProfile';
import { fetchRequest as refreshUser } from '../../redux/actions/user/getUser';
import { getUserTimezone, installTimezoneShim } from '../../utils/datetime';

// All IANA timezones when the runtime supports it; a pragmatic list
// otherwise (older Safari).
const timezoneOptions = () => {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (_e) { /* fall through */ }
  return [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
    'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
    'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo',
    'Australia/Sydney',
  ];
};

/* 28px tinted icon + overline — the shared Surface header pattern. */
const SurfaceHeader = ({ Icon, label }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(theme.palette.primary.main, 0.12),
        color: 'primary.main',
      }}>
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 1, fontWeight: 600, color: 'text.secondary' }}>
        {label}
      </Typography>
    </Stack>
  );
};

const PreferencesTab = () => {
  const dispatch = useDispatch();
  const { isDarkMode, toggleTheme } = useAppTheme();
  const user = useSelector((s) => s.user_getUser?.response?.user) || {};
  const saveState = useSelector((s) => s.auth?.updateProfile);

  const [timezone, setTimezone] = useState('');
  const [saved, setSaved] = useState(false);

  // The effective timezone today: user-doc setting first, browser
  // fallback — the same resolution every date in the UI uses.
  const savedTimezone = user.timezone || getUserTimezone();

  // Seed the form once the user doc arrives.
  useEffect(() => {
    setTimezone(savedTimezone);
  }, [savedTimezone]);

  const zones = useMemo(timezoneOptions, []);
  const saving = Boolean(saveState?.isLoading);
  const dirty = timezone !== savedTimezone;

  // Flash the saved confirmation when a save round-trip completes.
  const prevSaving = React.useRef(false);
  useEffect(() => {
    if (prevSaving.current && !saving && !saveState?.error) {
      setSaved(true);
      dispatch(refreshUser());
      // Re-point moment's default zone at the new preference; the
      // Date.toLocale* shim reads it dynamically on every call.
      installTimezoneShim();
    }
    prevSaving.current = saving;
  }, [saving, saveState, dispatch]);

  const save = () => {
    setSaved(false);
    dispatch(updateProfile({ timezone }));
  };

  const nowInSelected = (() => {
    try {
      return new Date().toLocaleString(undefined, {
        timeZone: timezone || 'UTC',
        weekday: 'short', hour: 'numeric', minute: '2-digit',
        month: 'short', day: 'numeric',
      });
    } catch (_e) {
      return null;
    }
  })();

  return (
    <Stack spacing={2}>
      {/* ── Appearance ─────────────────────────────────────────────── */}
      <Surface sx={{ p: 2.5 }}>
        <SurfaceHeader Icon={isDarkMode ? DarkModeIcon : LightModeIcon} label="Appearance" />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ maxWidth: 420 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Dark mode</Typography>
            <Typography variant="caption" color="text.secondary">
              Switch between the light and dark theme.
            </Typography>
          </Box>
          <Switch
            checked={isDarkMode}
            onChange={toggleTheme}
            inputProps={{ 'aria-label': 'Toggle dark mode' }}
          />
        </Stack>
      </Surface>

      {/* ── Timezone ───────────────────────────────────────────────── */}
      <Surface sx={{ p: 2.5 }}>
        <SurfaceHeader Icon={PublicIcon} label="Timezone" />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Every date and time in the app — schedules, activity timestamps,
          forecast windows, invoices — renders in this timezone.
        </Typography>

        <Stack spacing={2} sx={{ maxWidth: 420 }}>
          <TextField
            select
            size="small"
            label="Timezone"
            value={zones.includes(timezone) ? timezone : 'UTC'}
            onChange={(e) => { setSaved(false); setTimezone(e.target.value); }}
          >
            {zones.map((z) => (
              <MenuItem key={z} value={z}>{z.replace(/_/g, ' ')}</MenuItem>
            ))}
          </TextField>
          {nowInSelected && (
            <Typography variant="caption" color="text.secondary">
              Current time in {timezone.replace(/_/g, ' ')}: <strong>{nowInSelected}</strong>
            </Typography>
          )}
          <Box>
            <CMButton
              startIcon={saving
                ? <CircularProgress size={14} color="inherit" />
                : <SaveIcon sx={{ fontSize: 16 }} />}
              disabled={saving || !dirty}
              onClick={save}
              sx={{ height: 34, fontSize: 13 }}
            >
              {saving ? 'Saving…' : 'Save preferences'}
            </CMButton>
          </Box>
          {saveState?.error && (
            <Alert severity="error">
              {String(saveState.error?.status || saveState.error?.message || saveState.error)}
            </Alert>
          )}
          {saved && !saveState?.error && (
            <Alert severity="success">
              Timezone saved — dates across the app now render in {timezone.replace(/_/g, ' ')}.
            </Alert>
          )}
        </Stack>
      </Surface>
    </Stack>
  );
};

export default PreferencesTab;
